import { AirParticles } from '@/components/Layout';
import { Building2, CheckCircle2, ArrowRight, BarChart3, Shield, Wrench } from 'lucide-react';

export default function Builders({ onBookDemo }) {
  return (
    <div>
      <section className="relative pt-32 pb-20 px-6 lg:px-12 bg-teal-gradient overflow-hidden" data-testid="builders-hero">
        <AirParticles count={15} color="rgba(63,167,214,0.08)" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--aqua)' }}>For Builders & Developers</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6 animate-fade-up" style={{ fontFamily: 'Inter' }} data-testid="builders-title">
            Build healthier<br />spaces.
          </h1>
          <p className="text-base lg:text-lg text-white/50 max-w-2xl mx-auto mb-10 animate-fade-up delay-200">
            Integrate Vayu into your residential and commercial projects. Offer clean air as a feature, not an afterthought.
          </p>
          <button onClick={onBookDemo} className="btn-aqua inline-flex items-center gap-2 animate-fade-up delay-300" data-testid="builders-hero-cta">
            Partner With Us <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <section className="section-padding px-6 lg:px-12" data-testid="builders-benefits">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="overline-teal mb-4">Why Partner With Vayu</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Inter' }}>Competitive advantage through clean air.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Building2, title: 'Premium Positioning', desc: 'Differentiate your project with built-in air quality. Buyers pay 8-15% premium for health-focused homes.', color: 'var(--deep-teal)' },
              { icon: Shield, title: 'Green Certification', desc: 'Vayu installations support LEED, IGBC, and GRIHA certification requirements for indoor air quality.', color: 'var(--aqua)' },
              { icon: BarChart3, title: 'Marketing Asset', desc: 'Live AQI dashboards in your model apartment. Real data sells better than promises.', color: '#8B5CF6' },
            ].map((b) => (
              <div key={b.title} className="card-hover p-8 animate-fade-up" data-testid={`benefit-${b.title}`}>
                <b.icon className="w-8 h-8 mb-5" style={{ color: b.color }} strokeWidth={1.5} />
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Inter' }}>{b.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding px-6 lg:px-12 bg-subtle" data-testid="builders-process">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="overline-teal mb-4">Integration Process</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Inter' }}>Seamless construction integration.</h2>
          </div>
          <div className="space-y-0">
            {[
              { step: '01', title: 'Design Consultation', desc: 'Our engineering team works with your architects to plan Vayu integration during the design phase.' },
              { step: '02', title: 'Bulk Manufacturing', desc: 'Custom-sized Vayu Pane units manufactured to match your window specifications.' },
              { step: '03', title: 'Installation & Testing', desc: 'Professional installation by our trained team. Each unit tested for filtration efficiency.' },
              { step: '04', title: 'Dashboard Setup', desc: 'Centralized monitoring dashboard for building management. Real-time AQI for every unit.' },
            ].map((s) => (
              <div key={s.step} className="flex gap-6 items-start py-8" style={{ borderBottom: s.step !== '04' ? '1px solid var(--border)' : 'none' }} data-testid={`process-${s.step}`}>
                <span className="stat-number text-5xl flex-shrink-0" style={{ color: 'var(--aqua)', opacity: 0.2 }}>{s.step}</span>
                <div>
                  <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Inter' }}>{s.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding px-6 lg:px-12 text-center" data-testid="builders-cta">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Inter' }}>Ready to build cleaner?</h2>
          <p className="text-base mb-8" style={{ color: 'var(--text-secondary)' }}>Our team is ready to discuss integration plans for your next project.</p>
          <button onClick={onBookDemo} className="btn-vayu inline-flex items-center gap-2 text-base px-10 py-4" data-testid="builders-final-cta">
            Schedule a Meeting <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </div>
  );
}
